/**
 * Utility functions for converting notification data from backend format to frontend format
 */

export interface NotificationMetadata {
  [key: string]: any;
}

export interface BackendNotification {
  notifications_id: string;
  notification_source_id: string | null;
  notification_source: string | null;
  recipient_id: string;
  event_type_id: string;
  notification_text_id: string;
  metadata: NotificationMetadata;
  triggered_by: string | null;
  notification_content: string | null;
  tags: string | null;
  status_text: string;
  status: 'read' | 'unread';
  recipient_platform: string | null;
  timestamp: string;
  notification_text: {
    content: string;
  };
  event_types: {
    event_type: string;
    event_category: string | null;
    event_type_description: string | null;
  };
}

export interface ProcessedNotification extends BackendNotification {
  message: string;
}

/**
 * Converts template variables in notification content to actual values from metadata
 * @param content - The notification content with template variables like {{variable_name}}
 * @param metadata - The metadata object containing the actual values
 * @returns The processed content with variables replaced
 */
export function convertNotificationContent(
  content: string,
  metadata: NotificationMetadata
): string {
  if (!content || !metadata) {
    return content || '';
  }

  let processedContent = content;

  // Replace template variables in the format {{variable_name}}
  Object.keys(metadata).forEach((key) => {
    const value = metadata[key];
    if (value !== null && value !== undefined) {
      // Handle different data types
      let displayValue = value;

      if (
        typeof value === 'string' &&
        value.includes('T') &&
        value.includes('Z')
      ) {
        // Likely an ISO date string, format it nicely
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            displayValue = date.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            });
          }
        } catch (error) {
          // If date parsing fails, use the original value
          displayValue = value;
        }
      }

      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      processedContent = processedContent.replace(regex, String(displayValue));
    }
  });

  return processedContent;
}

/**
 * Processes a single notification from backend format to frontend format
 * @param notification - The raw notification from the API
 * @returns The processed notification with converted message content
 */
export function processNotification(
  notification: BackendNotification
): ProcessedNotification {
  const message = convertNotificationContent(
    notification.notification_text.content,
    notification.metadata
  );

  return {
    ...notification,
    message
  };
}

/**
 * Processes an array of notifications from backend format to frontend format
 * @param notifications - Array of raw notifications from the API
 * @returns Array of processed notifications with converted message content
 */
export function processNotifications(
  notifications: BackendNotification[]
): ProcessedNotification[] {
  return notifications.map(processNotification);
}

/**
 * Formats date values in metadata for better display
 * @param metadata - The metadata object
 * @returns The metadata with formatted date values
 */
export function formatMetadataDates(
  metadata: NotificationMetadata
): NotificationMetadata {
  const formattedMetadata = { ...metadata };

  Object.keys(formattedMetadata).forEach((key) => {
    const value = formattedMetadata[key];
    if (
      typeof value === 'string' &&
      value.includes('T') &&
      value.includes('Z')
    ) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          // Format based on the key name for better context
          if (
            key.toLowerCase().includes('date') ||
            key.toLowerCase().includes('time')
          ) {
            formattedMetadata[key] = date.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            });
          }
        }
      } catch (error) {
        // Keep original value if formatting fails
      }
    }
  });

  return formattedMetadata;
}
